import { useLocation } from "react-router-dom";
import { format } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import ClientService from "../../../services/clients/ClientService";
import {
  AccountLevelTheme,
  AccountLevelThemeCounts,
  ThemeCategoryData,
  ThemeCategoryConfig,
  ThemeCategoryKey,
  ThemeInsight,
  ThemeInsightSentiment,
  ThemeSectionProps,
} from "../../../interfaces/themes";
import ThemeInsightsTimeline from "./ThemeInsightsTimeline";
import ThemeTabs from "./ThemeTabs";
import "../../../styles/themes.css";
import useDisplaySnackbar from "../../../utils/useDisplaySnackbar";
import { formatDurationFromSeconds } from "../../../utils/utils";

const THEME_CATEGORIES: ThemeCategoryConfig[] = [
  {
    key: "executiveEscalations",
    label: "Executive Escalations",
    classification: "execEscalation",
    getCount: (counts) => counts.execEscalationTopicCount,
    getInteractionCount: (counts) => counts.execEscalationInteractionCount,
  },
  {
    key: "managerEscalations",
    label: "Manager Escalations",
    classification: "managerEscalation",
    getCount: (counts) => counts.managerEscalationTopicCount,
    getInteractionCount: (counts) => counts.managerEscalationInteractionCount,
  },
  {
    key: "operationalRisks",
    label: "Operational Risks",
    classification: "operationalRisk",
    getCount: (counts) => counts.operationalRiskTopicCount,
    getInteractionCount: (counts) => counts.operationalRiskInteractionCount,
  },
  {
    key: "clientControllable",
    label: "Client Controllable",
    classification: "clientControllable",
    getCount: (counts) => counts.clientControllableTopicCount,
    getInteractionCount: (counts) => counts.clientControllableInteractionCount,
  },
  {
    key: "positive",
    label: "Positive",
    classification: "positive",
    getCount: (counts) => counts.positiveTopicCount,
    getInteractionCount: (counts) => counts.positiveInteractionCount,
  },
];

const toSentiment = (sentiment?: string): ThemeInsightSentiment | undefined => {
  const normalizedSentiment = sentiment?.trim().toLowerCase();
  if (!normalizedSentiment) return undefined;

  if (normalizedSentiment === "positive") return "positive";
  if (normalizedSentiment === "negative") return "negative";
  if (normalizedSentiment === "neutral") return "neutral";

  return normalizedSentiment;
};

const toInsight = (theme: AccountLevelTheme, label: string): ThemeInsight => {
  let formattedDate = "-";
  if (theme.startTime) {
    const dateObj = new Date(theme.startTime);
    if (!isNaN(dateObj.getTime())) {
      formattedDate = format(dateObj, "dd MMM yyyy").toUpperCase();
    }
  }

  return {
    id: theme?.interactionId,
    date: formattedDate,
    interactionName: theme?.interactionName,
    callout: formatDurationFromSeconds(theme.actualDuration ?? 0)
      ?.replace(/\b0s\b/g, "")
      ?.replace(/\s+/g, " ")
      ?.trim(),
    summary: theme?.rationale,
    sentiment: toSentiment(theme.overallSentiment),
    tag: label,
  };
};

const ThemeSection: React.FC<ThemeSectionProps> = ({
  startTime,
  endTime,
  subOrgId,
  initialCategory = "executiveEscalations",
  orgId,
  clientName,
  activeView = "themes",
  parentPageIdentifier,
  customDateRange,
  startEndMeetingTime,
  accountViewState,
  isSlideOverOpen,
  slideOverParent,
}) => {
  const location = useLocation();
  const restoredCategory =
    (location.state?.themesActiveKey as ThemeCategoryKey) ?? initialCategory;
  const { showSnackbar } = useDisplaySnackbar();
  const [activeKey, setActiveKey] = useState<ThemeCategoryKey>(restoredCategory);

  useEffect(() => {
    if (initialCategory && !location.state?.themesActiveKey) {
      setActiveKey(initialCategory);
    }
  }, [initialCategory, location.state?.themesActiveKey]);
  const [themeCounts, setThemeCounts] = useState<
    AccountLevelThemeCounts | undefined
  >();
  const activeCategory = THEME_CATEGORIES.find(
    (category) => category.key === activeKey,
  )!;
  const { data, isLoading, isError, error } =
    ClientService.useGetAccountLevelThemes(
      {
        startTime,
        endTime,
        classification: activeCategory.classification,
        subOrgId: subOrgId ?? "",
      },
      Boolean(startTime && endTime && subOrgId),
    );

  useEffect(() => {
    if (isError) {
      showSnackbar(
        "error",
        error?.response?.data?.detail || error?.message || "Failed to fetch themes",
      );
    }
  }, [error, isError]);

  useEffect(() => {
    if (data?.counts) {
      setThemeCounts(data.counts);
    }
  }, [data]);

  const categories = useMemo<ThemeCategoryData[]>(
    () =>
      THEME_CATEGORIES.map((category) => ({
        key: category?.key,
        label: category?.label,
        count: themeCounts ? category.getCount(themeCounts) : 0,
        interactionCount: themeCounts
          ? category.getInteractionCount(themeCounts)
          : 0,
        insights:
          category.key === activeKey
            ? (data?.themes ?? []).map((theme) =>
              toInsight(theme, category.label),
            )
            : [],
      })),
    [activeKey, data, themeCounts],
  );

  const selectedCategory =
    categories.find((category) => category.key === activeKey) ?? categories[0];

  const navigationContext = useMemo(
    () => ({
      activeView,
      themesActiveKey: activeKey,
      customDateRange,
      startEndMeetingTime,
      clientName,
      orgId,
      subOrgId,
      accountViewState,
      parentPageIdentifier,
      isSlideOverOpen,
      slideOverParent,
    }),
    [
      activeView,
      activeKey,
      customDateRange,
      startEndMeetingTime,
      clientName,
      orgId,
      subOrgId,
      accountViewState,
      parentPageIdentifier,
      isSlideOverOpen,
      slideOverParent,
    ],
  );

  return (
    <section className="theme-section">
      <div className="theme-content">
        <ThemeTabs
          categories={categories}
          activeKey={activeKey}
          onChange={setActiveKey}
          isLoading={isLoading && !themeCounts}
        />
        {selectedCategory && (
          <ThemeInsightsTimeline
            category={selectedCategory}
            insights={selectedCategory.insights}
            isLoading={isLoading}
            navigationContext={navigationContext}
          />
        )}
      </div>
    </section>
  );
};

export default ThemeSection;
